/**
 * `useClickOutside` hook for capturing clicking outside of an element
 */
import { RefObject } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import { coerceArray } from '../../utils/UNSAFE_arrayUtils';

type Props = {
  /**
   * If true outside click is disabled
   */
  isDisabled?: boolean;
  /**
   * Reference to element or array of elements relative to which outside click is performed
   */
  ref: RefObject<HTMLElement> | RefObject<HTMLElement>[];
  /**
   * Outside click callback
   */
  handler?: (event: MouseEvent) => void;
};

const isPointOutside = (event: Event, ref: RefObject<HTMLElement>[]) => {
  const target = event.target as HTMLElement;
  if (target) {
    const doc = getDocument(target);
    if (!doc.contains(target)) return false;
  }
  return ref.every((elRef) => !elRef.current?.contains(target));
};

const getDocument = (node?: Node): Document => node?.ownerDocument ?? document;

export const useOutsideClick = ({ isDisabled: disabled = false, ref, handler }: Props) => {
  // use ref to store mutable data and not cause a re-render
  const stateRef = useRef({
    isMouseDown: false
  });

  const state = stateRef.current;

  useEffect(() => {
    const refs = coerceArray(ref);
    if (
      !handler ||
      disabled ||
      (refs.length > 0 && refs.findIndex((elRef) => elRef.current === null) > -1)
    )
      return;
    const onMouseDown = () => {
      state.isMouseDown = true;
    };

    const onMouseUp = (event: MouseEvent) => {
      if (state.isMouseDown && isPointOutside(event, refs)) {
        state.isMouseDown = false;
        handler?.(event);
      }
    };
    const firstElRef = refs[0];
    const doc = getDocument(firstElRef.current!);
    if (doc) {
      doc.addEventListener('mousedown', onMouseDown, true);
      doc.addEventListener('mouseup', onMouseUp, true);

      return () => {
        doc.removeEventListener('mousedown', onMouseDown, true);
        doc.removeEventListener('mouseup', onMouseUp, true);
      };
    }
    return undefined;
  }, [disabled, handler, ref, state]);
};
